// src/pages/LiveClasses/LiveClassesPage.tsx
import React, { useState, useMemo, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link }   from "react-router-dom";
import { ICourse } from "../../Courses/CoursesPage";
import TestPriceModal from "../../../components/Modals/TestPricingModal";
import LoginModal     from "../../../components/Auth/LoginModel";
import "./LiveClassesPage.css";

const API_BASE = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001/api/v1";

/* ── Helpers ── */
function formatScheduled(iso: string): string {
  return new Date(iso).toLocaleString("en-IN", {
    weekday:"short", day:"numeric", month:"short",
    hour:"2-digit", minute:"2-digit", hour12:true,
  });
}
function timeUntil(iso: string): string {
  const diff = new Date(iso).getTime() - Date.now();
  if (diff < 0) return "Started";
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  if (h > 24) return `in ${Math.floor(h/24)}d ${h%24}h`;
  if (h > 0)  return `in ${h}h ${m}m`;
  return `in ${m} min`;
}

const STATUS_TABS = [
  { key:"all",       label:"All Classes" },
  { key:"live",      label:"🔴 Live Now" },
  { key:"scheduled", label:"Upcoming" },
  { key:"ended",     label:"Recordings" },
];
const CATEGORIES = ["All","Engineering","Medical","Kids Programming","SSC/Banking","Programming","Languages","School"];

const SkeletonCard = () => (
  <div className="lcp-skeleton">
    <div className="lcp-skel-img" />
    <div className="lcp-skel-body">
      <div className="lcp-skel-line short" /><div className="lcp-skel-line" /><div className="lcp-skel-line medium" />
    </div>
  </div>
);

/* ════════════════════════════════════════════
   COMPONENT
════════════════════════════════════════════ */
const LiveClassesPage: React.FC = () => {
  const [classes,      setClasses]     = useState<ICourse[]>([]);
  const [loading,      setLoading]     = useState(true);
  const [statusTab,    setStatusTab]   = useState("all");
  const [category,     setCategory]    = useState("All");
  const [search,       setSearch]      = useState("");
  const [enrolledIds,  setEnrolledIds] = useState<Set<string>>(new Set());

  /* ── Payment / Login modal state ── */
  const [showLoginModal,   setShowLoginModal]   = useState(false);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [selectedClass,    setSelectedClass]    = useState<ICourse | null>(null);

  /* ── Fetch live classes from courses API ── */
  useEffect(() => {
    setLoading(true);
    fetch(`${API_BASE}/courses?is_live=true`)
      .then(r => r.json())
      .then(d => { if (d.success) setClasses(d.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  /* ── Check which classes user is enrolled in ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const user  = localStorage.getItem("user");
    if (!token || !user) return;
    //const userId = JSON.parse(user).id;
    classes.forEach(lc => {
      fetch(`${API_BASE}/courses/${lc.slug}/payment-status`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then(r => r.json())
        .then(d => { if (d.success && d.data.paid) setEnrolledIds(p => { const n = new Set(p); n.add(lc._id); return n; }); })
        .catch(() => {});
    });
  }, [classes]);

  const liveCount = classes.filter(c => c.status === "live").length;

  const filtered = useMemo(() => classes.filter(c => {
    const statusMatch = statusTab === "all" || c.status === statusTab;
    const catMatch    = category  === "All" || c.category === category;
    const srcMatch    = !search   || c.title.toLowerCase().includes(search.toLowerCase()) ||
                        c.instructor.toLowerCase().includes(search.toLowerCase());
    return statusMatch && catMatch && srcMatch;
  }), [classes, statusTab, category, search]);

  /* ── Enroll click — same LoginModal + TestPriceModal pattern as TestSeries ── */
  const handleEnrollClick = (lc: ICourse) => {
    const token = localStorage.getItem("token");
    if (!token) {
      localStorage.setItem("postLoginModelRedirect", window.location.pathname);
      setSelectedClass(lc);
      setShowLoginModal(true);
      return;
    }
    if (enrolledIds.has(lc._id)) return; // already enrolled
    setSelectedClass(lc);
    setShowPricingModal(true);
  };

  const handleLoginSuccess = () => {
    if (!selectedClass) return;
    setShowPricingModal(true);
  };

  return (
    <>
      <Helmet>
        <title>Live Classes – Acadebility</title>
        <meta name="description" content="Join live interactive classes with expert instructors in real-time." />
      </Helmet>

      <div className="lcp-page">

        {/* HERO */}
        <div className="lcp-hero">
          <div className="lcp-hero-inner">
            {liveCount > 0 && (
              <div className="lcp-live-now-banner">
                <span className="lcp-live-dot" />
                {liveCount} class{liveCount > 1 ? "es" : ""} happening right now
              </div>
            )}
            <h1 className="lcp-hero-title">Live Interactive Classes</h1>
            <p className="lcp-hero-sub">
              Learn in real-time with India's best educators. Ask questions, get instant answers, and learn with a community.
            </p>
            <div className="lcp-hero-stats">
              {[
                { val:`${liveCount}`,         label:"Live Now",       emoji:"🔴" },
                { val:`${classes.filter(c=>c.status==="scheduled").length}`, label:"Upcoming", emoji:"📅" },
                { val:"1L+",                  label:"Students",       emoji:"👥" },
                { val:"4.9★",                 label:"Avg Rating",     emoji:"⭐" },
              ].map(s => (
                <div className="lcp-stat" key={s.label}>
                  <span className="lcp-stat-emoji">{s.emoji}</span>
                  <span className="lcp-stat-val">{s.val}</span>
                  <span className="lcp-stat-label">{s.label}</span>
                </div>
              ))}
            </div>
            <div className="lcp-search-wrap">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
              <input className="lcp-search" placeholder="Search classes or instructors…" value={search} onChange={e => setSearch(e.target.value)} />
              {search && <button className="lcp-search-clear" onClick={() => setSearch("")}>✕</button>}
            </div>
          </div>
        </div>

        {/* BODY */}
        <div className="lcp-body">

          {/* Status tabs */}
          <div className="lcp-status-tabs">
            {STATUS_TABS.map(t => (
              <button key={t.key} className={`lcp-status-tab${statusTab===t.key?" active":""}`} onClick={() => setStatusTab(t.key)}>
                {t.label}
                <span className="lcp-tab-count">
                  {t.key==="all" ? classes.length : classes.filter(c => c.status === t.key).length}
                </span>
              </button>
            ))}
          </div>

          {/* Category chips */}
          <div className="lcp-cats">
            {CATEGORIES.map(c => (
              <button key={c} className={`lcp-cat${category===c?" active":""}`} onClick={() => setCategory(c)}>{c}</button>
            ))}
          </div>

          <div className="lcp-results-bar">
            <span className="lcp-results-count">Showing <strong>{filtered.length}</strong> classes</span>
          </div>

          {/* Grid */}
          {loading ? (
            <div className="lcp-grid">{Array.from({length:6}).map((_,i) => <SkeletonCard key={i} />)}</div>
          ) : filtered.length === 0 ? (
            <div className="lcp-empty"><span style={{fontSize:"3rem"}}>📡</span><h3>No classes found</h3><p>Try adjusting your filters</p></div>
          ) : (
            <div className="lcp-grid">
              {filtered.map(lc => {
                const isLive      = lc.status === "live";
                const isEnded     = lc.status === "ended";
                const isCancelled = lc.status === "cancelled";
                const isEnrolled  = enrolledIds.has(lc._id);
                const enrolled    = lc.enrolled_count || 0;
                const spots       = Math.max(0, (lc.max_participants || 500) - enrolled);
                const almostFull  = spots > 0 && spots < 20;

                return (
                  <div className={`lcp-card${isLive?" lcp-card-live":""}`} key={lc._id}>
                    {/* Thumbnail */}
                    <div className="lcp-card-img-wrap">
                      <img src={lc.image_url} alt={lc.title} className="lcp-card-img" loading="lazy" />
                      {isLive && <span className="lcp-badge lcp-badge-live"><span className="lcp-live-dot-sm"/>LIVE</span>}
                      {isEnded && lc.recording_available && <span className="lcp-badge lcp-badge-rec">⏺ Recording</span>}
                      {isCancelled && <span className="lcp-badge lcp-badge-cancelled">Cancelled</span>}
                      {lc.is_free && !isLive && <span className="lcp-badge lcp-badge-free">FREE</span>}
                      <span className="lcp-card-dur">{lc.duration_minutes || 60} min</span>
                    </div>

                    {/* Body */}
                    <div className="lcp-card-body">
                      <div className="lcp-card-cat">{lc.category}</div>
                      {/* FIX: link uses slug — same as CourseDetail */}
                      <Link to={`/live-classes/${lc.slug}`} className="lcp-card-title-link">
                        <h3 className="lcp-card-title">{lc.title}</h3>
                      </Link>
                      <div className="lcp-card-instructor">
                        <div className="lcp-instr-avatar">{lc.instructor_avatar}</div>
                        <div>
                          <div className="lcp-instr-name">{lc.instructor}</div>
                          {lc.instructor_title && <div className="lcp-instr-title">{lc.instructor_title}</div>}
                        </div>
                      </div>

                      {!isEnded && !isCancelled && (
                        <div className={`lcp-card-time${isLive?" lcp-card-time-live":""}`}>
                          {isLive ? (
                            <><span className="lcp-live-dot-sm"/>In Progress · {enrolled} joined</>
                          ) : lc.scheduled_at ? (
                            <><span className="lcp-time-icon">📅</span>{formatScheduled(lc.scheduled_at)} · <strong>{timeUntil(lc.scheduled_at)}</strong></>
                          ) : null}
                        </div>
                      )}

                      <div className="lcp-card-meta-row">
                        <span className="lcp-card-enrolled">👥 {enrolled} enrolled</span>
                        {almostFull && <span className="lcp-spots-warn">🔥 Only {spots} spots left!</span>}
                        {spots === 0 && !isLive && <span className="lcp-spots-full">Full</span>}
                      </div>

                      <div className="lcp-tags">
                        {(lc.tags || []).slice(0,3).map((t:any) => <span className="lcp-tag" key={t}>{t}</span>)}
                      </div>

                      {/* CTA buttons */}
                      <div className="lcp-card-cta">
                        {isCancelled ? (
                          <button className="lcp-btn lcp-btn-disabled" disabled>Cancelled</button>
                        ) : isEnded ? (
                          lc.recording_available
                            ? <Link to={`/live-classes/${lc.slug}`} className="lcp-btn lcp-btn-rec">▶ Watch Recording</Link>
                            : <button className="lcp-btn lcp-btn-disabled" disabled>Recording Soon</button>
                        ) : isLive ? (
                          isEnrolled
                            ? <Link to={`/live-classes/${lc.slug}/join`} className="lcp-btn lcp-btn-join">🔴 Join Now</Link>
                            : <button className="lcp-btn lcp-btn-enroll-live" onClick={() => handleEnrollClick(lc)}>Enroll & Join</button>
                        ) : (
                          isEnrolled
                            ? <button className="lcp-btn lcp-btn-enrolled" disabled>✓ Enrolled</button>
                            : <button className="lcp-btn lcp-btn-enroll" onClick={() => handleEnrollClick(lc)}>
                                {lc.is_free ? "Enroll Free" : "Enroll Now"}
                              </button>
                        )}
                        <Link to={`/live-classes/${lc.slug}`} className="lcp-btn lcp-btn-details">Details</Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* LoginModal — same as TestSeriesNormal */}
      {showLoginModal && (
        <LoginModal
          show={showLoginModal}
          onClose={() => setShowLoginModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      )}

      {/* TestPriceModal — same as TestSeriesNormal */}
      {showPricingModal && selectedClass && (
        <TestPriceModal
          show={showPricingModal}
          onClose={() => setShowPricingModal(false)}
          discounted_price={String(selectedClass.price)}
          original_price={String(selectedClass.original_price)}
          blogTitle={selectedClass.title}
          test_category_name={selectedClass.category}
          onCheckout={() => {
            setShowPricingModal(false);
            setEnrolledIds(p => { const n = new Set(p); n.add(selectedClass._id); return n; });
          }}
        />
      )}
    </>
  );
};

export default LiveClassesPage;
